import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faUser, 
  faEnvelope, 
  faPhone, 
  faIdCard, 
  faMapMarkerAlt,
  faExclamationTriangle,
  faFileContract,
  faCalendarAlt,
  faMoneyBillWave,
  faSignOutAlt,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { tenantService } from '../../services/tenantService';
import { contractService } from '../../services/contractService';
import { formatCurrency } from '../../utils/currencyFormatter';

const TenantDetail = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [tenant, setTenant] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTenantDetails();
  }, [tenantId]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch tenant details
      const tenantData = await tenantService.getTenantById(tenantId);
      setTenant(tenantData);
      
      // Fetch tenant's contracts
      const contractsData = await contractService.getContractsByTenant(tenantId);
      setContracts(contractsData);
    } catch (error) {
      console.error('Error fetching tenant details:', error);
      setError('Failed to fetch tenant details: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleEdit = () => {
    navigate(`/manager/tenants/${tenantId}/edit`);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete tenant "${tenant?.name}"? This action cannot be undone.`)) {
      // Navigate to tenant management with delete flag
      navigate('/manager/tenants', { state: { deleteTenantId: tenantId } });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/tenants')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Tenants
        </Button>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Tenant not found
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/manager/tenants')}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/manager/tenants')}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Tenants
          </Button>
          <div>
            <h2 className="mb-1">Tenant Details</h2>
            <p className="text-muted mb-0">View tenant information and contracts</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={handleEdit}>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Tenant
          </Button>
          <Button variant="outline-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Tenant
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </Button>
        </div>
      </div>

      <Row>
        {/* Tenant Profile */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUser} className="text-primary me-2" />
                Tenant Profile
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Full Name:</strong>
                    <div className="text-muted">{tenant.name}</div>
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                      {tenant.email}
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Phone:</strong>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faPhone} className="me-1" />
                      {tenant.phone}
                    </div>
                  </div>
                  {tenant.idNumber && (
                    <div className="mb-3">
                      <strong>ID Number:</strong>
                      <div className="text-muted">
                        <FontAwesomeIcon icon={faIdCard} className="me-1" />
                        {tenant.idNumber}
                      </div>
                    </div>
                  )}
                </Col>
                <Col md={6}>
                  {tenant.address && (
                    <div className="mb-3">
                      <strong>Address:</strong>
                      <div className="text-muted">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                        {tenant.address}
                      </div>
                    </div>
                  )}
                  {tenant.emergencyContact && (
                    <div className="mb-3">
                      <strong>Emergency Contact:</strong>
                      <div className="text-muted">{tenant.emergencyContact}</div>
                      {tenant.emergencyPhone && (
                        <div className="text-muted">
                          <FontAwesomeIcon icon={faPhone} className="me-1" />
                          {tenant.emergencyPhone}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div>
                      <Badge bg={tenant.status === 'active' ? 'success' : 'secondary'}>
                        {tenant.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="mb-3">
                    <strong>Created:</strong>
                    <div className="text-muted">
                      {tenant.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </div>
                  </div>
                </Col>
              </Row>
              {tenant.notes && (
                <div className="mt-3">
                  <strong>Notes:</strong>
                  <div className="text-muted mt-1">{tenant.notes}</div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Contracts */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
                Contracts ({contracts.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {contracts.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faFileContract} className="text-muted mb-3" size="3x" />
                  <h6>No contracts found</h6>
                  <p className="text-muted">This tenant has no active contracts.</p>
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/manager/contracts/create', { state: { tenantId } })}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Create Contract
                  </Button>
                </div>
              ) : (
                <div>
                  {contracts.map((contract) => (
                    <Card key={contract.id} className="mb-3 border">
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Property Unit:</strong>
                              <div className="text-muted">{contract.propertyUnitId || 'N/A'}</div>
                            </div>
                            <div className="mb-2">
                              <strong>Start Date:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>End Date:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                            {contract.contractDuration && (
                              <div className="mb-2">
                                <strong>Duration:</strong>
                                <div className="text-muted">{contract.contractDuration}</div>
                              </div>
                            )}
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Monthly Rent:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                                {formatCurrency(contract.chequeAmount || 0, contract.currency || 'USD')}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Total Amount:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                                {formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD')}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Status:</strong>
                              <div>
                                <Badge bg={contract.status === 'active' ? 'success' : 'secondary'}>
                                  {contract.status}
                                </Badge>
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Contract ID:</strong>
                              <div className="text-muted small">{contract.contractId || contract.id}</div>
                            </div>
                          </Col>
                        </Row>
                        <div className="mt-3">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => navigate(`/manager/contracts/${contract.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">Quick Actions</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button 
                  variant="primary"
                  onClick={() => navigate('/manager/contracts/create', { state: { tenantId } })}
                >
                  <FontAwesomeIcon icon={faFileContract} className="me-2" />
                  Create New Contract
                </Button>
                <Button variant="outline-primary" onClick={handleEdit}>
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Tenant
                </Button>
                <Button variant="outline-danger" onClick={handleDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Delete Tenant
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h6 className="mb-0">Statistics</h6>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <h4 className="mb-1">{contracts.length}</h4>
                <p className="text-muted mb-0">Total Contracts</p>
              </div>
              <hr />
              <div className="text-center">
                <h4 className="mb-1">
                  {formatCurrency(
                    contracts.reduce((sum, contract) => sum + (contract.chequeAmount || 0), 0),
                    contracts[0]?.currency || 'USD'
                  )}
                </h4>
                <p className="text-muted mb-0">Total Monthly Rent</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
  };
  
export default TenantDetail; 